import React, { useEffect, useState } from "react";
import { X } from "lucide-react";
import { Button, TextArea } from "./Primitives";

/**
 * Lightweight confirm dialog that captures a required "reason" string.
 * Replaces window.prompt() in admin flows so testing agents + mobile users
 * get a clean modal instead of a browser prompt.
 *
 * Props:
 *   open                — boolean
 *   title               — dialog headline
 *   description         — body text explaining the action
 *   confirmLabel        — CTA text (defaults "Confirm")
 *   cancelLabel         — cancel text (defaults "Cancel")
 *   tone                — "gold" | "danger" | "emerald" — CTA button colour
 *   reasonLabel         — label for the reason textarea
 *   reasonPlaceholder   — placeholder
 *   reasonRequired      — whether the CTA is disabled without a reason (default true)
 *   maxLength           — reason max chars (default 500)
 *   onConfirm({reason}) — invoked with the captured reason (async allowed)
 *   onClose()           — invoked on cancel or backdrop click
 *   testId              — root data-testid
 */
export default function ConfirmReasonDialog({
  open,
  title,
  description,
  confirmLabel = "Confirm",
  cancelLabel = "Cancel",
  tone = "gold",
  reasonLabel = "Reason (visible in audit log)",
  reasonPlaceholder = "",
  reasonRequired = true,
  maxLength = 500,
  onConfirm,
  onClose,
  testId = "confirm-reason-dialog",
}) {
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (open) { setReason(""); setBusy(false); }
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  if (!open) return null;

  const canConfirm = !reasonRequired || reason.trim().length > 0;
  const toneClasses = {
    gold: "!bg-[#8A6600] hover:!bg-[#6B4E00] !text-white",
    emerald: "!bg-emerald-700 hover:!bg-emerald-800 !text-white",
    danger: "!bg-rose-700 hover:!bg-rose-800 !text-white",
  }[tone] || "";

  const submit = async () => {
    if (!canConfirm || busy) return;
    setBusy(true);
    try {
      await onConfirm({ reason: reason.trim() });
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/45 backdrop-blur-sm animate-in fade-in"
      onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}
      data-testid={testId}
    >
      <div className="w-full max-w-md bg-white rounded-lg shadow-xl border border-navy/15 animate-in zoom-in-95">
        <div className="flex items-start justify-between p-5 border-b border-navy/10">
          <div className="min-w-0 pr-4">
            <div className="font-heading text-ink text-lg leading-tight" data-testid={`${testId}-title`}>{title}</div>
            {description && (
              <div className="text-sm text-ink-soft mt-1 leading-relaxed" data-testid={`${testId}-description`}>
                {description}
              </div>
            )}
          </div>
          <button
            onClick={onClose}
            className="p-1 text-ink-soft hover:text-ink hover:bg-navy/5 rounded"
            data-testid={`${testId}-close`}
            aria-label="Close"
          ><X size={18} /></button>
        </div>
        <div className="p-5 space-y-2">
          <TextArea
            label={reasonLabel}
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            rows={3}
            maxLength={maxLength}
            placeholder={reasonPlaceholder}
            testId={`${testId}-reason`}
          />
          <div className="text-[10px] font-mono text-ink-faint text-right">{reason.length}/{maxLength}</div>
        </div>
        <div className="px-5 py-4 border-t border-navy/10 flex items-center justify-end gap-2">
          <Button variant="secondary" onClick={onClose} disabled={busy} testId={`${testId}-cancel`}>
            {cancelLabel}
          </Button>
          <Button
            onClick={submit}
            disabled={!canConfirm || busy}
            className={toneClasses}
            testId={`${testId}-confirm`}
          >
            {busy ? "Working…" : confirmLabel}
          </Button>
        </div>
      </div>
    </div>
  );
}
